import { RefObject, useEffect, useRef, useState } from 'react';
import { StreamBounds } from '../../../features/quickstart/types/theme';

const ZERO_BOUNDS: StreamBounds = { width: 0, height: 0, top: 0, left: 0 };

/**
 * Observes a video element's rendered bounding rect.
 * Updates on resize, fullscreen change, orientation change.
 * Returns { width: 0, height: 0, top: 0, left: 0 } until element mounts.
 *
 * Uses requestAnimationFrame batching to coalesce rapid ResizeObserver
 * callbacks into a single state update per frame.
 */
export function useStreamBounds(
  elementRef: RefObject<HTMLElement | null>,
): StreamBounds {
  const [bounds, setBounds] = useState<StreamBounds>(ZERO_BOUNDS);
  const rafIdRef = useRef<number>(0);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      setBounds(ZERO_BOUNDS);
      return;
    }

    const measure = () => {
      const rect = element.getBoundingClientRect();
      setBounds((prev) => {
        if (
          prev.width === rect.width &&
          prev.height === rect.height &&
          prev.top === rect.top &&
          prev.left === rect.left
        ) {
          return prev;
        }
        return {
          width: rect.width,
          height: rect.height,
          top: rect.top,
          left: rect.left,
        };
      });
    };

    const scheduleMeasure = () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(measure);
    };

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(element);

    document.addEventListener('fullscreenchange', scheduleMeasure);
    window.addEventListener('orientationchange', scheduleMeasure);

    // Initial measurement
    measure();

    return () => {
      observer.disconnect();
      document.removeEventListener('fullscreenchange', scheduleMeasure);
      window.removeEventListener('orientationchange', scheduleMeasure);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [elementRef]);

  return bounds;
}
